"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { ClientSessionContext } from "../lib/auth/ClientSessionContext"
import { reportOperationError } from "../lib/logging"

// Keying visible data by identity prevents a previous account's response from
// appearing during the render before effect cleanup runs.
export default function useAccountResource<T>(
  url: string | null,
  resourceScope: string | null,
) {
  const credentials = useContext(ClientSessionContext)
  const key = url && resourceScope ? JSON.stringify([resourceScope, url]) : null
  const [state, setState] = useState<{
    key: string
    data: T | null
    error: unknown
    loading: boolean
  } | null>(null)
  const pending = useRef<AbortController | null>(null)
  const refetch = useCallback(async () => {
    if (!key || !url) return
    pending.current?.abort()
    const controller = new AbortController()
    pending.current = controller
    setState((previous) => ({
      key,
      data: previous?.key === key ? previous.data : null,
      error: null,
      loading: true,
    }))
    try {
      if (!credentials) throw new Error("Account requests require SessionManager")
      const token = await credentials.getToken()
      if (controller.signal.aborted || !token) return
      const response = await fetch(url, {
        headers: { "x-auth-token": token },
        signal: controller.signal,
      })
      if (!response.ok)
        throw new Error(`Account request failed (${response.status})`)
      const data = (await response.json()) as T
      if (!controller.signal.aborted)
        setState({ key, data, error: null, loading: false })
    } catch (error) {
      if (!controller.signal.aborted) {
        setState({ key, data: null, error, loading: false })
        reportOperationError(error, {
          errorType: "account_request_error",
          endpoint: url.split("?")[0],
        })
      }
    }
  }, [key, url, credentials])
  useEffect(() => {
    // Synchronize request state, including explicit refetches, with this identity.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refetch()
    const unsubscribe = credentials?.subscribe(() => pending.current?.abort())
    return () => {
      unsubscribe?.()
      pending.current?.abort()
    }
  }, [refetch, credentials])
  return {
    data: key && state?.key === key ? state.data : null,
    error: key && state?.key === key ? state.error : null,
    loading: !!key && (state?.key !== key || state.loading),
    refetch,
  }
}
